/**
 * SocialLoginButtons - Social authentication buttons
 * Google, LinkedIn, Apple, Facebook
 * Icone con colore brand gold (#b08b5c)
 */

import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import supabase from '../../lib/supabase';
import { toast } from 'sonner';

// Brand color for icons
const BRAND_COLOR = '#b08b5c';

// Social provider icons as SVG - Unified gold color
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5">
    <path fill={BRAND_COLOR} d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill={BRAND_COLOR} fillOpacity="0.8" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill={BRAND_COLOR} fillOpacity="0.6" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill={BRAND_COLOR} fillOpacity="0.9" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill={BRAND_COLOR}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill={BRAND_COLOR}>
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill={BRAND_COLOR}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const SocialLoginButtons = ({ mode = 'register' }) => {
  const [loading, setLoading] = useState(null);
  
  const handleSocialLogin = async (provider) => {
    setLoading(provider);
    
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: provider === 'google' ? {
            access_type: 'offline',
            prompt: 'consent',
          } : undefined
        }
      });
      
      if (error) {
        throw error;
      }
      
      // OAuth will redirect, no need to handle success here
    } catch (error) {
      console.error(`${provider} login error:`, error);
      toast.error(`Errore ${provider}: ${error.message}`);
      setLoading(null);
    }
  };
  
  const SocialButton = ({ provider, icon: Icon, label, bgColor, textColor = 'text-white' }) => (
    <button
      type="button"
      onClick={() => handleSocialLogin(provider)}
      disabled={loading !== null}
      className={`flex items-center justify-center gap-3 w-full h-11 rounded-lg border border-white/20 transition-all ${
        loading === provider 
          ? 'bg-white/10 cursor-wait' 
          : 'bg-white/5 hover:bg-white/10 hover:border-white/30'
      }`}
    >
      {loading === provider ? (
        <Loader2 className="w-5 h-5 animate-spin text-white/60" />
      ) : (
        <>
          <Icon />
          <span className="text-sm text-white/80">{label}</span>
        </>
      )}
    </button>
  );
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <SocialButton 
          provider="google" 
          icon={GoogleIcon} 
          label="Google"
        />
        <SocialButton 
          provider="linkedin_oidc" 
          icon={LinkedInIcon} 
          label="LinkedIn"
        />
        <SocialButton 
          provider="apple" 
          icon={AppleIcon} 
          label="Apple"
        />
        <SocialButton 
          provider="facebook" 
          icon={FacebookIcon} 
          label="Facebook"
        />
      </div>
      
      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-white/10" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="px-4 bg-[#0a0a0f] text-white/40">
            oppure {mode === 'register' ? 'registrati' : 'accedi'} con email
          </span>
        </div>
      </div>
    </div>
  );
};

export default SocialLoginButtons;
